import { Box, Button, Typography } from "@mui/material";
import React, { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import CollapseSection from "../../../components/ui/CollapseSection";
import {
   getCustomerGroupDetailRecord,
   getModificationCustomerRows,
} from "./cgpgUtils";

function formatValidityPeriod(startDate, endDate) {
   if (!startDate || !endDate) {
      return "";
   }

   const formatDate = (value) =>
      new Date(value).toLocaleDateString("en-UK", {
         year: "numeric",
         month: "2-digit",
         day: "2-digit",
      });

   return `${formatDate(startDate)} - ${formatDate(endDate)}`;
}

function getCustomerColor(isActive) {
   if (isActive === 1) {
      return "#008000";
   }

   if (isActive === -1) {
      return "#C00000";
   }

   return "#000000";
}

function filterCustomerRows(customerRows, customerFilter) {
   if (customerFilter === "added") {
      return customerRows.filter((item) => item.isActive === 1);
   }

   if (customerFilter === "deleted") {
      return customerRows.filter((item) => item.isActive === -1);
   }

   return customerRows;
}

export default function CustomerGroupSummary() {
   const { groupRowClicked, selectedView, cG_Details, cgModificationDetails } =
      useSelector((store) => store.cgpgApproval);
   const customerGroupDetail = getCustomerGroupDetailRecord(cG_Details);
   const [customerRows, setCustomerRows] = useState([]);
   const [customerFilter, setCustomerFilter] = useState("all");
   const [isAscByName, setAscByName] = useState(false);
   const [isAscByNumber, setAscByNumber] = useState(false);

   useEffect(() => {
      setCustomerRows(getModificationCustomerRows(cgModificationDetails));
      setCustomerFilter("all");
      setAscByName(false);
      setAscByNumber(false);
   }, [cgModificationDetails]);

   const displayedCustomerRows = useMemo(
      () => filterCustomerRows(customerRows, customerFilter),
      [customerRows, customerFilter]
   );

   if (!groupRowClicked || selectedView !== "Customer Group") {
      return null;
   }

   const handleCustomerSort = (type) => {
      if (type === "name") {
         const list = [...customerRows].sort((a, b) => {
            const comparison = a.customer.toLowerCase().trim().localeCompare(
               b.customer.toLowerCase().trim()
            );
            return isAscByName ? -comparison : comparison;
         });

         setCustomerRows(list);
         setAscByName(!isAscByName);
         return;
      }

      const list = [...customerRows].sort((a, b) => {
         const comparison = String(a.customerNumber ?? "").localeCompare(
            String(b.customerNumber ?? ""),
            undefined,
            { numeric: true, sensitivity: "base" }
         );
         return isAscByNumber ? -comparison : comparison;
      });

      setCustomerRows(list);
      setAscByNumber(!isAscByNumber);
   };

   return (
      <Box sx={{ mt: 2 }}>
         <CollapseSection title="Summary">
            <Box
               sx={{
                  fontSize: "14px",
                  ml: 2,
                  mt: 2,
               }}
            >
               <LabelValue
                  label="Customer Group"
                  value={customerGroupDetail?.customerGroupName ?? ""}
               />
               <LabelValue
                  label="Validity Period"
                  value={formatValidityPeriod(
                     customerGroupDetail?.effectiveStartDate,
                     customerGroupDetail?.effectiveEndDate
                  )}
               />
               <Box>
                  <Typography sx={{ fontWeight: "600", fontSize: "14px" }}>
                     Customer(s):{" "}
                  </Typography>
                  <Box
                     sx={{
                        display: "flex",
                        alignItems: "center",
                        flexWrap: "wrap",
                        gap: 1,
                        mb: 2,
                     }}
                  >
                     <Typography
                        sx={{ fontSize: "14px", cursor: "pointer" }}
                        onClick={() => handleCustomerSort("name")}
                        display="inline"
                     >
                        Sort by Name |
                     </Typography>
                     <Typography
                        sx={{ fontSize: "14px", cursor: "pointer" }}
                        onClick={() => handleCustomerSort("number")}
                        display="inline"
                     >
                        Sort by Number
                     </Typography>
                     <Button
                        variant={
                           customerFilter === "added"
                              ? "contained"
                              : "outlined"
                        }
                        onClick={() => setCustomerFilter("added")}
                        sx={{
                           textTransform: "none",
                           boxShadow: "none",
                           ...(customerFilter === "added"
                              ? {
                                   backgroundColor: "#008000",
                                   color: "#FFFFFF",
                                   "&:hover": {
                                      backgroundColor: "#006400",
                                      boxShadow: "none",
                                   },
                                }
                              : {
                                   borderColor: "#008000",
                                   color: "#008000",
                                   backgroundColor: "#FFFFFF",
                                   "&:hover": {
                                      borderColor: "#008000",
                                      backgroundColor:
                                         "rgba(0, 128, 0, 0.06)",
                                      boxShadow: "none",
                                   },
                                }),
                        }}
                     >
                        Added Customers
                     </Button>
                     <Button
                        variant={
                           customerFilter === "deleted"
                              ? "contained"
                              : "outlined"
                        }
                        onClick={() => setCustomerFilter("deleted")}
                        sx={{
                           textTransform: "none",
                           boxShadow: "none",
                           ...(customerFilter === "deleted"
                              ? {
                                   backgroundColor: "#C00000",
                                   color: "#FFFFFF",
                                   "&:hover": {
                                      backgroundColor: "#A00000",
                                      boxShadow: "none",
                                   },
                                }
                              : {
                                   borderColor: "#C00000",
                                   color: "#C00000",
                                   backgroundColor: "#FFFFFF",
                                   "&:hover": {
                                      borderColor: "#C00000",
                                      backgroundColor:
                                         "rgba(192, 0, 0, 0.06)",
                                      boxShadow: "none",
                                   },
                                }),
                        }}
                     >
                        Deleted Customers
                     </Button>
                     <Button
                        variant="outlined"
                        onClick={() => setCustomerFilter("all")}
                        sx={{
                           textTransform: "none",
                           borderColor: "#BDBDBD",
                           color: "#000000",
                           backgroundColor: "#FFFFFF",
                           boxShadow: "none",
                           "&:hover": {
                              borderColor: "#BDBDBD",
                              backgroundColor: "#FFFFFF",
                              boxShadow: "none",
                           },
                        }}
                     >
                        Clear Filter
                     </Button>
                  </Box>
                  <Box
                     sx={{
                        border: "1px solid #C4C4C4",
                        borderRadius: 1,
                        minHeight: 180,
                        maxHeight: 180,
                        overflowY: "auto",
                        p: 1.5,
                        fontSize: "14px",
                        fontWeight: 400,
                     }}
                  >
                     {displayedCustomerRows.map((item) => (
                        <Box
                           key={`${item.id}-${item.customer}`}
                           sx={{
                              color: getCustomerColor(item.isActive),
                              whiteSpace: "pre-wrap",
                              lineHeight: 1.5,
                           }}
                        >
                           {item.customer}
                        </Box>
                     ))}
                  </Box>
               </Box>
            </Box>
         </CollapseSection>
      </Box>
   );
}

function LabelValue({ label, value }) {
   return (
      <div>
         <strong>{label}:</strong>&nbsp;{value}
      </div>
   );
}
